import {
  action,
  computed,
  reaction,
  observable,
} from 'mobx';
import { LocalStorage } from '../utils/local-storage';
import { setMonitoring } from '../utils/datadog';
import type { Location } from '../../types/api/Location';
import type { Plant } from '../../types/api/Plant';
import type { AtLeast } from '../../types/helpers/AtLeast';
import RootStore from './root-store';
import { sortByField } from '../utils/sort-by-field';

const LOCATION_ID_STORAGE_KEY = 'location_id';
const PLANT_ID_STORAGE_KEY = 'plant_id';

class LocationsStore {
  rootStore: RootStore;
  localStorage: LocalStorage;
  @observable
    locations: Location[];
  @observable
    plantId: number | null;
  @observable
    locationId: number | null;
  @observable
    loadingLocations: boolean;
  @observable
    divisionCode: number | null;
  @observable
    plantChoiceFormSubmitted: boolean;
  private locationsFetchReaction: () => void;

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore;
    this.localStorage = new LocalStorage('locations-store');
    this.locations = [];
    this.plantId = null;
    this.locationId = null;
    this.divisionCode = null;
    this.loadingLocations = false;
    this.plantChoiceFormSubmitted = false;

    this.locationsFetchReaction = this.setupLocationsFetchReaction();
  }

  private setupLocationsFetchReaction(): () => void {
    return reaction(
      () => ({
        // Fetch locations when the auth token changes
        authToken: this.rootStore.newAuthStore.authToken,
        // Company config is necessary to ensure the client gets locations from tenanted API
        companyConfig: this.rootStore.newAuthStore.companyConfig,
      }),
      async ({ authToken, companyConfig }) => {
        // Fetch locations if the auth token is set and the locations haven't been fetched yet
        if (authToken && companyConfig && !this.locations.length) {
          await this.getLocations();

          // Set the location and plant IDs from local storage
          const storedLocationId = this.localStorage.getItem(LOCATION_ID_STORAGE_KEY);
          if (!storedLocationId) return;
          this.setLocationId(Number(storedLocationId));
          const storedPlantId = this.localStorage.getItem(PLANT_ID_STORAGE_KEY);
          if (!storedPlantId) return;
          this.setPlantId(Number(storedPlantId));
        }
      },
      { fireImmediately: false },
    );
  }

  @computed
  get currentLocation(): AtLeast<Location, 'plants'> {
    return this.locations.find(location => location.id === this.locationId) || { plants: [] };
  }

  @computed
  get plantsForCurrentLocation(): Plant[] {
    return this.currentLocation.plants || [];
  }

  @computed
  get currentPlant(): Partial<Plant> {
    return this.currentLocation.plants.find(plant => plant.id === this.plantId) || {};
  }

  @action
  getPlantsForLocationId(locationId: number): Plant[] {
    const location = this.locations.find(location => location.id === locationId);

    if (!location) {
      return [];
    }

    return location.plants || [];
  }

  @action
  setPlantId(plantId: number | string | null) {
    if (!plantId) {
      this.plantId = null;
      this.localStorage.removeItem(PLANT_ID_STORAGE_KEY);
      return;
    }
    this.plantId = Number(plantId);
    this.localStorage.setItem(PLANT_ID_STORAGE_KEY, this.plantId.toString());

    // @ts-ignore | For console debugging purposes
    window.SYSDYNE_STORAGE = this.storage;
  }

  @action
  setLocationId(locationId: number | string) {
    if (!locationId) {
      this.locationId = null;
      this.localStorage.removeItem(LOCATION_ID_STORAGE_KEY);
      return;
    }
    this.locationId = Number(locationId);
    this.localStorage.setItem(LOCATION_ID_STORAGE_KEY, this.locationId.toString());

    // TODO: Implicit call. This function shouldn't be called here
    setMonitoring(this.rootStore.newAuthStore.companyConfig?.appService, this.currentPlant.description, this.rootStore.newAuthStore.user);
  }

  @action
  async getLocations() {
    this.loadingLocations = true;
    let locations: any = await this.rootStore.newAuthStore.makeAuthorizedRequest('/api/location?plants=true');
    let sortedLocations: Location[] = sortByField(locations, 'name');
    let sortedLocationsWithSortedPlants = sortedLocations.map((location: Location) => {
      location.plants = sortByField(location.plants, 'description');
      return location;
    });
    this.locations = sortedLocationsWithSortedPlants;
    this.loadingLocations = false;
  };

  @action
  async getDivision() {
    if (!this.locationId) {
      return null;
    }

    const location = this.locations.find(v => v.id === this.locationId);
    const divisionId = location && location.divisionIds && location.divisionIds.length ? location.divisionIds[0] : '';

    const response:any = await this.rootStore.newAuthStore.makeAuthorizedRequest(`/api/division/${divisionId}`);
    this.divisionCode = response ? response.code : null;
  }
}

export default LocationsStore;
